import { apiService } from './api';
import { PlanFile, WrmpChecks, WrmpPerson, WrmpPlan, WrmpReview } from '../types';

export type WrmpPlanUpdate = Partial<Pick<WrmpPlan, 'version' | 'responsible_persons' | 'amendments_log' | 'next_review_date'>>;
export interface WrmpReviewPayload {
  review_date: string;
  persons_present: WrmpPerson[];
  checks: WrmpChecks;
  problems_changes?: string;
  next_review_date?: string;
  signature: string;
}

export const DIAGRAM_ACCEPT = '.png,.jpg,.jpeg,.gif,.tif,.tiff,.webp,.pdf';
export const ATTACHMENT_ACCEPT = `${DIAGRAM_ACCEPT},.doc,.docx,.xls,.xlsx,.txt,.csv`;
export const DEFAULT_MAX_FILE_SIZE = 25 * 1024 * 1024;
export const WRMP_STATEMENT = 'I confirm I have reviewed and understood the Water Risk Management Plan and will implement its requirements.';

/** Download authenticated evidence using its original filename. */
export async function saveWrmpFile(planId: string, file: Pick<PlanFile, 'id' | 'filename'>): Promise<void> {
  const blob = await wrmpService.downloadFile(planId, file.id);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  try {
    anchor.href = url;
    anchor.download = file.filename;
    document.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(url);
  }
}

export const wrmpService = {
  getPlans: (filters: {client_id?: string; manager_id?: string}) => apiService.get<WrmpPlan[]>("/wrmp", filters),
  getPlan(facilityId: string): Promise<WrmpPlan> {
    return apiService.get<WrmpPlan>(`/facilities/${facilityId}/wrmp`);
  },
  updatePlan(planId: string, payload: WrmpPlanUpdate): Promise<WrmpPlan> {
    return apiService.patch<WrmpPlan>(`/wrmp/${planId}`, payload);
  },
  async uploadFile(planId: string, file: File, kind: PlanFile['kind'], maximum = DEFAULT_MAX_FILE_SIZE): Promise<PlanFile> {
    if (file.size > maximum) throw new Error(`File is too large (max ${maximum / 1024 / 1024} MB)`);
    const body = new FormData();
    body.append('file', file);
    body.append('kind', kind);
    const response = await fetch(`${apiService.getBaseUrl()}/wrmp/${planId}/files`, {
      method: 'POST', headers: await apiService.authHeaders(), body,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(typeof error.detail === 'string' ? error.detail : response.status === 413 ? `File is too large (max ${maximum / 1024 / 1024} MB)` : 'Unable to upload file');
    }
    return response.json();
  },
  fileUrl(planId: string, fileId: string): string {
    return `${apiService.getBaseUrl()}/wrmp/${planId}/files/${fileId}`;
  },
  async downloadFile(planId: string, fileId: string): Promise<Blob> {
    const response = await fetch(this.fileUrl(planId, fileId), { headers: await apiService.authHeaders() });
    if (!response.ok) throw new Error(`Failed to download WRMP file: ${response.status}`);
    return response.blob();
  },
  deleteFile(planId: string, fileId: string): Promise<void> {
    return apiService.delete<void>(`/wrmp/${planId}/files/${fileId}`);
  },
  createReview(planId: string, payload: WrmpReviewPayload): Promise<WrmpReview> {
    return apiService.post<WrmpReview>(`/wrmp/${planId}/reviews`, payload);
  },
  voidReview(reviewId: string, reason: string): Promise<WrmpReview> {
    return apiService.post<WrmpReview>(`/wrmp-reviews/${reviewId}/void`, { reason });
  },
  acknowledgeReview(reviewId: string, signature: string): Promise<WrmpReview> {
    return apiService.post<WrmpReview>(`/wrmp-reviews/${reviewId}/acknowledge`, { signature });
  },
};
